import React, { useEffect, useState } from 'react';
import { Card, Badge, Button } from '../components/UI';
import { apiRequest } from '../utils/api';
import { RefreshCw, Trophy, AlertTriangle, Calendar, Grid3X3, Flag, Users, Sparkles } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { PageHero } from '../components/PageHero';
import { useLeague } from '../components/LeagueContext';
import { hasActiveSeason, NoActiveSeason } from '../components/NoActiveSeason';

interface HeatmapData {
    events: { id: string, round_number: number, location: string, flag_url: string, status: string, is_sprint: boolean | number }[];
    drivers: { id: string, username: string, team_name: string, team_color: string, total_points: number }[];
    matrix: Record<string, Record<string, any>>;
}

export const Heatmap: React.FC = () => {
    const { seasons, isLoading: leagueLoading } = useLeague();
    const [data, setData] = useState<HeatmapData | null>(null);
    const [loading, setLoading] = useState(true);

    const loadData = () => {
        setLoading(true);
        apiRequest<HeatmapData>('season_matrix.php')
            .then(res => setData(res))
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    useEffect(() => { loadData(); }, []);

    const getUrl = (url?: string) => {
        if (!url) return undefined;
        if (url.startsWith('http') || url.startsWith('data:')) return url;
        const baseUrl = API_BASE_URL === '.' ? '' : API_BASE_URL;
        if (baseUrl && !url.startsWith('/')) return `${baseUrl}/${url}`;
        if (baseUrl) return `${baseUrl}${url}`;
        return url;
    };

    const getCellColor = (res: any, eventStatus: string) => {
        if (!res) {
            if (eventStatus === 'COMPLETED') return 'bg-white/[0.02] border border-white/[0.04]';
            return 'bg-transparent border border-white/[0.02] opacity-40';
        }
        if (res.status === 'DNF' || res.status === 'DSQ') return 'bg-red-500/15 text-red-400 border border-red-500/20';
        const pos = parseInt(res.position);
        if (pos === 1) return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/40 font-bold';
        if (pos === 2) return 'bg-gray-400/15 text-gray-300 border border-gray-400/30 font-bold';
        if (pos === 3) return 'bg-orange-500/15 text-orange-400 border border-orange-500/30 font-bold';
        if (res.points > 0) return 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20';
        return 'bg-white/[0.03] text-white/40 border border-white/[0.05]';
    };

    if (loading || leagueLoading) return <div className="p-20 text-center"><RefreshCw className="animate-spin mx-auto text-white/20"/></div>;
    if (!hasActiveSeason(seasons)) return (
        <div className="sdc-feature-page sdc-heatmap-page max-w-7xl mx-auto animate-fade-in pb-20 space-y-6">
            <PageHero eyebrow="Saisonpause" title="Performance-Matrix" description="Die Heatmap wird mit den Ergebnissen der nächsten laufenden Saison gefüllt." icon={Grid3X3} accent="#22c55e" stats={[{ label: 'Aktive Saison', value: '–' }, { label: 'Rennen', value: 0 }, { label: 'Ergebnisse', value: 0 }]} />
            <NoActiveSeason description="Aktuell gibt es keine laufende Saison für die Heatmap. Vergangene Rennverläufe, Tabellen und Einzelergebnisse findest du in der Historie." />
        </div>
    );
    if (!data || data.events.length === 0) return <div className="p-20 text-center text-white/30">Keine Saisondaten.</div>;

    const completedEvents = data.events.filter(event => event.status === 'COMPLETED').length;
    const resultCount = Object.values(data.matrix).reduce((sum, driverResults) => sum + Object.keys(driverResults || {}).length, 0);

    return (
        <div className="sdc-feature-page sdc-heatmap-page max-w-7xl mx-auto animate-fade-in pb-20 space-y-4 md:space-y-6">
            <PageHero
                eyebrow="Saisonanalyse"
                title="Performance-Matrix"
                description="Jedes Rennen, jeder Fahrer und jedes Ergebnis in einer kompakten Saisonkarte. Farben zeigen Podien, Punkte und Ausfälle auf einen Blick."
                icon={Grid3X3}
                accent="#22c55e"
                stats={[{ label: 'Fahrer', value: data.drivers.length }, { label: 'Rennen', value: completedEvents }, { label: 'Ergebnisse', value: resultCount }]}
                actions={<Button variant="secondary" size="sm" icon={RefreshCw} onClick={loadData}>Aktualisieren</Button>}
            >
                <div className="mt-5 flex flex-wrap gap-2 text-[9px] font-bold uppercase tracking-wider text-white/40">
                    <span className="rounded-full border border-yellow-500/20 bg-yellow-500/10 px-3 py-1.5 text-yellow-300"><Trophy size={10} className="mr-1 inline"/> Podium</span>
                    <span className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1.5 text-emerald-300"><Sparkles size={10} className="mr-1 inline"/> Punkte</span>
                    <span className="rounded-full border border-red-500/20 bg-red-500/10 px-3 py-1.5 text-red-300"><AlertTriangle size={10} className="mr-1 inline"/> DNF / DSQ</span>
                </div>
            </PageHero>

            {/* Desktop Table */}
            <div className="sdc-bento-card hidden overflow-x-auto md:block">
                <table className="w-full text-left border-collapse min-w-max">
                    <thead>
                        <tr>
                            <th className="sticky left-0 z-20 bg-[#111118] border-b border-r border-white/[0.04] p-3 w-[200px]">
                                <div className="text-[9px] text-white/20 uppercase font-semibold tracking-wider">Fahrer</div>
                            </th>
                            {data.events.map(evt => (
                                <th key={evt.id} className="p-1.5 border-b border-white/[0.04] text-center w-[60px] min-w-[60px] bg-[#0c0c12]">
                                    <div className="text-[10px] text-white/30 font-mono">R{evt.round_number}</div>
                                    <div className="text-[7px] text-white/15 uppercase mt-0.5 truncate max-w-[56px] mx-auto">{evt.location}</div>
                                </th>
                            ))}
                            <th className="sticky right-0 z-20 p-2 border-b border-l border-white/[0.06] text-center w-[70px] bg-[#0c0c12]">
                                <div className="text-[9px] text-white/30 uppercase font-semibold">Pkt</div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.drivers.map((driver, idx) => (
                            <tr key={driver.id} className="hover:bg-white/[0.02] transition-colors group">
                                <td className="sticky left-0 z-10 bg-[#111118] border-r border-white/[0.04] p-2.5 group-hover:bg-white/[0.02] transition-colors">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[9px] font-mono text-white/15 w-4">{idx + 1}</span>
                                        <div className="w-0.5 h-6 rounded-full" style={{backgroundColor: driver.team_color}}/>
                                        <div className="min-w-0">
                                            <div className="font-semibold text-white/90 text-xs truncate">{driver.username}</div>
                                            <div className="text-[8px] text-white/20 truncate">{driver.team_name}</div>
                                        </div>
                                    </div>
                                </td>
                                {data.events.map(evt => {
                                    const res = data.matrix[driver.id]?.[evt.id];
                                    const isSprint = evt.is_sprint === true || Number(evt.is_sprint) === 1;
                                    return (
                                        <td key={evt.id} className="p-0.5">
                                            <div className={`w-full h-11 flex flex-col items-center justify-center rounded-lg text-[11px] transition-transform hover:scale-105 cursor-default relative ${getCellColor(res, evt.status)}`}>
                                                {res ? (<>
                                                    <div className="flex items-center gap-0.5">
                                                        <span className="font-black leading-none">{res.status === 'FINISHED' ? res.position : res.status.substring(0,3)}</span>
                                                        {isSprint && res.sprint_position && parseInt(res.sprint_position) > 0 && (
                                                            <span className="text-[8px] text-f1-red font-bold border-l border-white/10 pl-0.5 ml-0.5">{res.sprint_status === 'FINISHED' ? res.sprint_position : 'X'}</span>
                                                        )}
                                                    </div>
                                                    <span className="text-[8px] opacity-60 leading-none mt-0.5">{parseFloat(res.points||0) + parseFloat(res.sprint_points||0) + parseFloat(res.penalty_total||0)}</span>
                                                    {res.fastest_lap && <div className="absolute top-0.5 right-0.5 w-1 h-1 bg-purple-500 rounded-full"/>}
                                                </>) : (
                                                    evt.status === 'COMPLETED' ? <span className="text-[8px] text-white/10">DNP</span> : <span className="text-white/5">-</span>
                                                )}
                                            </div>
                                        </td>
                                    );
                                })}
                                <td className="sticky right-0 z-10 p-1 border-l border-white/[0.04] text-center bg-[#0c0c12] group-hover:bg-white/[0.02]">
                                    <span className="font-mono font-bold text-white text-xs">{parseFloat(driver.total_points as any).toFixed(0)}</span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Mobile View - Card based */}
            <div className="md:hidden space-y-2">
                {data.drivers.map((driver, idx) => {
                    const driverResults = data.events.map(evt => ({ evt, res: data.matrix[driver.id]?.[evt.id] }));
                    const totalPts = parseFloat(driver.total_points as any).toFixed(0);
                    return (
                        <details key={driver.id} className="group overflow-hidden rounded-[1.35rem] border border-white/[0.07] bg-gradient-to-br from-[#151923] to-[#0f1119] shadow-lg">
                            <summary className="flex items-center gap-2.5 px-3 py-2.5 cursor-pointer list-none">
                                <span className="text-[10px] font-mono text-white/20 w-5">{idx + 1}</span>
                                <div className="w-0.5 h-6 rounded-full shrink-0" style={{backgroundColor: driver.team_color}}/>
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs font-bold text-white/90 truncate">{driver.username}</div>
                                    <div className="text-[8px] text-white/20">{driver.team_name}</div>
                                </div>
                                <span className="font-mono font-bold text-white text-sm shrink-0">{totalPts}</span>
                            </summary>
                            <div className="px-3 pb-3 pt-1">
                                <div className="flex flex-wrap gap-1">
                                    {driverResults.map(({ evt, res }) => (
                                        <div key={evt.id} className={`w-12 h-12 flex flex-col items-center justify-center rounded-lg text-[10px] ${getCellColor(res, evt.status)}`}>
                                            {res ? (<>
                                                <span className="text-[8px] text-white/20">R{evt.round_number}</span>
                                                <span className="font-black">{res.status === 'FINISHED' ? res.position : 'X'}</span>
                                                <span className="text-[7px] opacity-50">{parseFloat(res.points||0) + parseFloat(res.sprint_points||0) + parseFloat(res.penalty_total||0)}</span>
                                            </>) : (
                                                <><span className="text-[8px] text-white/20">R{evt.round_number}</span><span className="text-[8px] text-white/10">{evt.status === 'COMPLETED' ? '-' : '·'}</span></>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </details>
                    );
                })}
            </div>
        </div>
    );
};
